import { AlertTriangle } from 'lucide-react';
import * as React from 'react';
import { Button } from '@/components/ui/button';

interface State {
  error: Error | null;
}

/**
 * Catches render-time crashes so a bug in one screen shows a recoverable
 * message rather than a blank page.
 */
export class ErrorBoundary extends React.Component<{ children: React.ReactNode }, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // In production this is where an error reporter would be called.
    console.error('Unhandled UI error', error, info.componentStack);
  }

  override render(): React.ReactNode {
    if (!this.state.error) return this.props.children;

    return (
      <div className="flex min-h-screen items-center justify-center p-6">
        <div className="max-w-md space-y-4 text-center">
          <div className="mx-auto w-fit rounded-full bg-danger-soft p-3">
            <AlertTriangle className="size-6 text-danger" aria-hidden="true" />
          </div>
          <div className="space-y-1">
            <h1 className="text-lg font-semibold text-ink">Something went wrong</h1>
            <p className="text-sm text-ink-muted">
              This screen hit an unexpected error. Reloading usually clears it.
            </p>
          </div>
          <div className="flex justify-center gap-2">
            <Button onClick={() => window.location.reload()}>Reload the page</Button>
            <Button variant="secondary" onClick={() => this.setState({ error: null })}>
              Try again
            </Button>
          </div>
        </div>
      </div>
    );
  }
}
